import { ReCaptchaProvider } from 'next-recaptcha-v3';
import Image from 'next/image';
import { useMedia } from 'react-use';

import { SUPPORTED_CHAINS } from '@/constants/chain';

import { ENV } from '@/environment/client';

import { breakpoints } from '@/styles/util';

import { ResponsiveBR } from '../../ui/ResponsiveBR';
import { CrossChainLiquiditySection } from '../CrossChainSwaps/CrossChainLiquiditySection';
import { StyledPrimaryCell, StyledH2 } from '../CrossChainSwaps/utils';
import { MarketingPageHeader } from '../Header/MarketingPageHeader';
import { Footer } from '../Landing/LandingFooter';
import { MarketingFeaturesSection } from '../MarketingSection/MarketingFeaturesSection';
import { DEXAggregatorSection } from '../MatchaAuto/DEXAggregatorSection';
import { NewsletterInput } from '../NewsletterInput';
import { MatchaBlogSection } from './MatchaBlogSection';
import { SettingUpSection } from './SettingUpSection';

export function NewsletterSignup() {
  const isDesktop = useMedia(breakpoints.desktop, false);
  const isMobile = useMedia(breakpoints.mobile, false);

  return (
    <ReCaptchaProvider reCaptchaKey={ENV.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY}>
      <MarketingPageHeader />
      <main
        className="relative mx-auto mt-[168px] min-h-[calc(100vh-274px)] px-4 desktop:mt-[152px] desktop:px-4"
        data-lock-theme="true"
      >
        <div
          className="relative mx-auto mb-24 flex w-full max-w-[1240px] justify-center rounded-medium border border-solid border-token-gray-400 bg-token-gray-100 py-20 desktop:py-24"
          style={{
            background: `url(/images/newsletter-signup/signup-lines.svg) no-repeat center, var(--color-token-gray-100)`,
          }}
        >
          <div
            className="my-1.5 flex w-full max-w-[704px] flex-col items-center desktop:my-0.5"
            style={{
              background:
                'radial-gradient(951.02% 81.55% at 47.51% 69.43%, #FCFCFD 0%, rgba(252, 252, 253, 0.00) 100%)',
            }}
          >
            <h4
              className="text-center font-heading text-[20px] font-bold leading-[38px] text-primitive-green-800"
              style={{
                WebkitTextFillColor: 'transparent',
                background:
                  'linear-gradient(rgba(2, 168, 99, 1), rgba(2, 168, 99, 0.49)), linear-gradient(rgba(118, 182, 36, 1), rgba(36, 182, 77, 0))',
                backgroundClip: 'text',
              }}
            >
              Be confident onchain with
            </h4>
            <h1 className="mx-auto mb-6 grid max-w-[322px] text-center font-heading text-[46px] font-semibold leading-[48px] tablet:max-w-none tablet:text-[64px] tablet:leading-[80px]">
              Matcha Newsletter
            </h1>
            <p className="mx-auto mb-12 grid max-w-[322px] text-center font-body text-[16px] leading-[25px] font-normal text-token-gray-800 tablet:max-w-[480px]">
              Join over 20,000 subscribers for weekly tutorials, updates, and insights straight to
              your inbox!
            </p>
            <NewsletterInput />
          </div>
        </div>
        <div className="absolute left-1/2 top-4 w-full min-w-[375px] h-[460px] -translate-x-1/2 tablet:max-w-[856px] desktop:max-w-[1440px]">
          <Image
            src="/images/newsletter-signup/bg-left.png"
            alt=""
            width={isDesktop ? 300 : isMobile ? 130 : 185}
            height={isDesktop ? 310 : isMobile ? 136 : 192}
            className="absolute left-[-64px] top-[-80px] tablet:left-[-116px] tablet:top-auto tablet:bottom-4 desktop:left-[-118px] desktop:bottom-[-28px]"
          />
          <Image
            src="/images/newsletter-signup/bg-right.png"
            alt=""
            width={isDesktop ? 262 : isMobile ? 182 : 210}
            height={isDesktop ? 276 : isMobile ? 192 : 222}
            className="absolute right-[-105px] top-[-64px] tablet:right-[-164px] tablet:top-[-36px] desktop:right-[-72px] desktop:top-[-24px]"
          />
        </div>
        <CrossChainLiquiditySection
          title={
            <>
              Start safely and stay on top <ResponsiveBR /> of Web3
            </>
          }
          listItems={[
            'Short tutorials to onboard you to Web3',
            'Regular rundown of new features and updates',
            'Roundup of latest news, partnerships, and promotions',
          ]}
        />
        <StyledPrimaryCell className="flex flex-col items-center mt-5 px-0">
          <StyledH2 className="mx-6">What you can do with Matcha</StyledH2>
          <MarketingFeaturesSection
            swapText={`Zero fee instant swaps on ${SUPPORTED_CHAINS.length} networks.`}
            limitOrdersText="Set a price that suits you."
            crossChainText="Bridge easily to new chains."
          />
        </StyledPrimaryCell>

        <SettingUpSection />
        <MatchaBlogSection />
        <DEXAggregatorSection hideContentText />
      </main>
      <Footer />
    </ReCaptchaProvider>
  );
}
